/**
 * A request that cannot outlive its budget. Card #54415 (D3).
 *
 * WHY THIS EXISTS: `web/services/api.js` `post()` called `fetch()` with no `signal`, no
 * `AbortController` and no timeout. A checkout submit that hung left `loading` true forever — the
 * button read "Processing..." with no exit but a reload, and the member could not tell whether
 * they had paid. The audit called that out as the one path where a customer is left blind.
 *
 * Pure: it takes the request as a FUNCTION and returns its result. No `fetch`, no DOM, no module
 * state — which is what lets jest (`testEnvironment: 'node'`) execute the timeout path directly
 * instead of asserting against source text. `api.js` itself cannot be imported there
 * (extensionless `../config/supabase`, plus `import.meta.env` at module scope).
 */

/** Carried on the thrown error's `code` AND inside `data.code`, so both call-site idioms work. */
export const REQUEST_TIMEOUT_CODE = 'REQUEST_TIMEOUT';

export const REQUEST_TIMEOUT_MESSAGE =
  'The payment request timed out before the server answered. Your order may or may not have been placed — check your order history before trying again.';

/**
 * Run `request(signal)` under a deadline.
 *
 * `request` is expected to pass the signal it is given to `fetch`, so an abort actually cancels
 * the in-flight request instead of merely abandoning it.
 *
 * A caller-supplied `signal` is honoured too and is ⛔ NOT reported as a timeout: a timeout is a
 * specific, recoverable condition with its own copy, and mislabelling an unmount abort as one
 * would tell a member their payment timed out when nothing of the sort happened.
 */
export const runWithTimeout = async (request, { timeoutMs = 0, signal } = {}) => {
  const hasBudget = Number.isFinite(timeoutMs) && timeoutMs > 0;
  if (!hasBudget) return request(signal);

  const controller = new AbortController();
  let timedOut = false;

  const abortFromCaller = () => controller.abort();
  const callerAlreadyAborted = Boolean(signal?.aborted);
  if (callerAlreadyAborted) controller.abort();
  else signal?.addEventListener('abort', abortFromCaller);

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await request(controller.signal);
  } catch (error) {
    if (!timedOut) throw error;
    const timeoutError = new Error(REQUEST_TIMEOUT_MESSAGE);
    timeoutError.name = 'TimeoutError';
    timeoutError.code = REQUEST_TIMEOUT_CODE;
    timeoutError.status = 0;
    timeoutError.data = { code: REQUEST_TIMEOUT_CODE, message: REQUEST_TIMEOUT_MESSAGE };
    throw timeoutError;
  } finally {
    clearTimeout(timer);
    if (!callerAlreadyAborted) signal?.removeEventListener('abort', abortFromCaller);
  }
};
